/*
 * Copyright (c) 2020 Little Robot Friends, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {BaseProvider} from 'baseui'
import React from 'react'
import {Provider} from 'react-redux'
import {ReactReduxFirebaseProvider} from 'react-redux-firebase'
import {actionTypes, createFirestoreInstance} from 'redux-firestore'
import {Client as Styletron} from 'styletron-engine-atomic'
import {Provider as StyletronProvider} from 'styletron-react'
import {configureAppStore, firebase, wrapProviders} from '../../features/persistence'
import {lightTheme} from '../../utils/theme'

// Configure React-Redux store provider
const store = configureAppStore()

const WithStore = ({children, ...props}) => (
  <Provider store={store} {...props}>
    {children}
  </Provider>
)

// Configure React-Redux-Firebase provider
const rrfProps = {
  firebase,
  config: {
    attachAuthIsReady: true,
    enableClaims: true,
    keysToRemoveFromAuth: [],
    presence: 'presence',
    sessions: 'sessions',
    useFirestoreForProfile: true,
    useFirestoreForStorageMeta: true,
    userProfile: 'users',
    onAuthStateChanged: (authData, firebase, dispatch) => {
      if (!authData) {
        dispatch({type: actionTypes.CLEAR_DATA})
      }
    },
  },
  dispatch: store.dispatch,
  createFirestoreInstance,
}

const WithReactReduxFirebase = ({children, ...props}) => (
  <ReactReduxFirebaseProvider {...props} {...rrfProps}>
    {children}
  </ReactReduxFirebaseProvider>
)

// Configure Styletron engine
const engine = new Styletron()

const WithStyleTron = ({children, ...props}) => (
  <StyletronProvider value={engine} {...props}>
    {children}
  </StyletronProvider>
)

// Configure Base Web provider
const WithBaseProvider = ({children, ...props}) => (
  <BaseProvider
    theme={lightTheme}
    overrides={{
      AppContainer: {
        style: {
          alignContent: 'stretch',
          alignItems: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          flexWrap: 'nowrap',
          height: '100%',
          justifyContent: 'flex-start',
        },
      },
    }}
    {...props}
  >
    {children}
  </BaseProvider>
)

// Wrap all providers
const Providers = wrapProviders([
  WithStore,
  WithReactReduxFirebase,
  WithStyleTron,
  WithBaseProvider,
])

export {Providers as default}
